// src/pages/reports/AuditLogsReport.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Button, Stack, TextField, FormControl, MenuItem, InputLabel, Select } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { authenticationService } from 'src/services/authentication.service';
import { get,post } from 'src/services/httpClient.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import reportDownloadService from 'src/services/reportDownload.service';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
dayjs.locale('en-gb');

const UserActivityLogs = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [entityFilter, setEntityFilter] = useState('');
  const [entityOptions, setEntityOptions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('month'));
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();
  const [auditLoading, setAuditLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  // Format date consistently throughout the component
  const formatDisplayDate = (date: dayjs.Dayjs | null) => {
    if (!date) return '';
    return date.format('DD/MM/YYYY');
  };

  const formatDisplayDateTime = (date: any) => {
    if (!date) return '';
    return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
  };

  const formatApiDate = (date: dayjs.Dayjs | null) => {
    if (!date) return '';
    return date.format('YYYY-MM-DD');
  };

  const handleGenerateAuditReport = async () => {
  if (!startDate || !endDate) {
    showSnackbar('Please select start and end dates', 'error');
    return;
  }

  try {
    setAuditLoading(true);

    const payload = {
      reportType: 'AUDIT_LOGS',
      fromDate: startDate.format('YYYY-MM-DD'),
      toDate: endDate.format('YYYY-MM-DD'),
      vendorId,
      entityName: entityFilter || null
    };

    const response = await post(
      `${process.env.REACT_APP_BASE_API_URL}/api/audit-report/generate`,
      payload
    );

    if (response?.token) {
      showSnackbar(response.token, 'success');
    }
  } catch (error) {
    console.error(error);
    showSnackbar('Failed to generate audit report', 'error');
  } finally {
    setAuditLoading(false);
  }
};

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'action', headerName: 'Action', flex: 1, minWidth: 80 },
    { field: 'entityName', headerName: 'Entity Name', flex: 1, minWidth: 150 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    { field: 'modifiedDataJson', headerName: 'Detail', flex: 1, minWidth: 200 },
    { field: 'command', headerName: 'Command', flex: 1, minWidth: 150 },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 1, 
      minWidth: 120,
      valueGetter: (params) => {
        return params.row.status || '';
      }
    },
    { field: 'createdByName', headerName: 'Created By', flex: 1, minWidth: 150 },
    { 
      field: 'createdDateStr', 
      headerName: 'Created Date', 
      flex: 1, 
      minWidth: 170,
      valueGetter: (params) => {
        return formatDisplayDateTime(params.row.createdDate);
      }
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      try {
        const from = formatApiDate(startDate);
        const to = formatApiDate(endDate);

        const data = await get(
          
          `${process.env.REACT_APP_BASE_API_URL}/api/reports/vendor/${vendorId}/getAuditReportData?fromDate=${from}&toDate=${to}`
        );
        const response = data?.data
        const dataWithId = response.map((item: any, index: number) => {
          // Clean description & command: remove "wowza"
          const cleanDescription = item.description;
            // ? item.description.replace(/wowza/gi, "")
            // : "";
          const cleanCommand = item.command;
            // ? item.command.replace(/wowza/gi, "")
            // : "";

          return {
            id: index + 1,
            createdDateStr: formatDisplayDateTime(item.createdDate),
            ...item,
            description: cleanDescription.trim(),
            command: cleanCommand.trim(),
          };
        });

        console.log('dataWithId', dataWithId);

        setRows(dataWithId);
        // Extract unique entity names
        const entities = [...new Set(response.map((item: any) => item.entityName))] as string[];
        setEntityOptions(entities);
       
      } catch (error) {
        console.error('Error fetching audit log data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, vendorId]);

  const filteredRows = rows.filter((row) => {
    const matchesSearch = Object.values(row).some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesEntity =
      entityFilter === '' || row.entityName === entityFilter;

    return matchesSearch && matchesEntity;
  });

  // Single download handler for both formats using common service
  const handleDownload = async (format: 'pdf' | 'excel') => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      const from = formatDisplayDate(startDate);
      const to = formatDisplayDate(endDate);
      const fromApi = formatApiDate(startDate);
      const toApi = formatApiDate(endDate);
      
      // Generate filename with entity filter
      const entitySuffix = entityFilter ? `_${entityFilter}` : '_All';
      const customFileName = `UserActivityLogs${entitySuffix}_${fromApi}_to_${toApi}_${dayjs().format('HHmmss')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      const success = await reportDownloadService.downloadReport({
        title: 'User Activity Logs Report',
        columns,
        rows: filteredRows,
        month: startDate.month() + 1,
        year: startDate.year(),
        fileName: customFileName,
        
        // Excel specific configuration with consistent date formatting
        excelHeaders: [
          "ID",
          "Action",
          "Entity Name",
          "Description",
          "Detail",
          "Command",
          "Status",
          "Created By",
          "Created Date"
        ],
        excelRowMapper: (row: any) => [
          row.id || '',
          row.action || '',
          row.entityName || '',
          row.description || '',
          row.modifiedDataJson || '',
          row.command || '',
          row.status || '',
          row.createdByName || '',
          formatDisplayDateTime(row.createdDate) // Use consistent format
        ]
      }, format);
      
      if (!success) {
        alert(`Failed to generate ${format.toUpperCase()}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error generating ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  return (
    <>
      <Helmet>
        <title>User Activity Logs</title>
      </Helmet>

      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <PageTitle
              heading="User Activity Logs"
              subHeading="Track system operations, changes, and user activity logs"
              handleClick={undefined}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasPermission(["OWNER"]) && (
             <>
  <Button
    variant="contained"
    color="primary"
    startIcon={<DownloadIcon />}
    onClick={() => handleDownload('excel')}
    disabled={downloading.excel}
  >
    {downloading.excel ? 'Generating...' : 'Download Excel'}
  </Button>

  <Button
    variant="outlined"
    color="secondary"
    startIcon={<PictureAsPdfIcon />}
    onClick={() => handleDownload('pdf')}
    disabled={downloading.pdf}
  >
    {downloading.pdf ? 'Generating...' : 'Download PDF'}
  </Button>

  {/* <Button
    size="small"
    variant="contained"
    color="success"
    onClick={handleGenerateAuditReport}
    disabled={auditLoading}
    sx={{ minWidth: 140, height: 36 }}
  >
    {auditLoading ? 'Generating...' : 'Generate Report'}
  </Button> */}
</>
            )}
          </Box>
        </Box>
      </PageTitleWrapper>

      {/* Filter Section */}
      <Box sx={{ px: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Entity Name</InputLabel>
              <Select
                value={entityFilter}
                label="Entity Name"
                onChange={(e) => setEntityFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {entityOptions.map((name, index) => (
                  <MenuItem key={index} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePicker
              label="Start Date (DD/MM/YYYY)"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY'
                } 
              }}
            />
            <DatePicker
              label="End Date (DD/MM/YYYY)"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY'
                } 
              }}
            />
          </Stack>
        </LocalizationProvider>
      </Box>

      {/* Table Section */}
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%', padding: '15px' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          getRowHeight={() => 'auto'}
          sx={{
            boxShadow: 'none',
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell': {
              alignItems: 'start',
              py: 1.5
            },
          }}
        />
      </Box>
    </>
  );
};

export default UserActivityLogs;